// FILE: components/Grid.tsx
// This component provides a responsive grid layout with proper Tailwind classes.

import React from 'react';

type GridGap = 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
type GridProps<C extends React.ElementType> = { 
  as?: C; 
  children?: React.ReactNode; 
  className?: string; 
  gap?: GridGap; 
  columns?: number | { sm?: number; md?: number; lg?: number; xl?: number; }; 
};

const GridFC = <C extends React.ElementType = "div">({ 
  as, 
  children, 
  className = '', 
  gap = 6, 
  columns = 1, 
  ...rest 
}: GridProps<C> & Omit<React.ComponentPropsWithoutRef<C>, keyof GridProps<C>>) => {
  const Component = as || "div";
  
  // Map gap numbers to Tailwind classes
  const gapClasses = {
    1: 'gap-1',
    2: 'gap-2', 
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-5',
    6: 'gap-6',
    8: 'gap-8',
    10: 'gap-10',
    12: 'gap-12',
    16: 'gap-16',
    20: 'gap-20',
    24: 'gap-24',
    32: 'gap-32',
  };
  
  // Build responsive column classes
  const gridClasses = typeof columns === 'number' 
    ? `grid-cols-${columns}` 
    : [
        'grid-cols-1', // Default for xs
        columns.sm ? `sm:grid-cols-${columns.sm}` : '',
        columns.md ? `md:grid-cols-${columns.md}` : '',
        columns.lg ? `lg:grid-cols-${columns.lg}` : '',
        columns.xl ? `xl:grid-cols-${columns.xl}` : '',
      ].filter(Boolean).join(' ');
  
  const combinedClasses = `grid ${gapClasses[gap]} ${gridClasses} ${className}`.trim();
  
  return (
    <Component className={combinedClasses} {...rest}>
      {children}
    </Component>
  );
};

export const Grid = GridFC;
